import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useBranding, buildBrandingUrl } from "../contexts/BrandingContext";
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  UserCog,
  FolderTree,
  ChevronDown,
  ChevronRight,
  LogOut,
  Building2,
  Settings,
  CreditCard,
  Handshake,
  Wallet,
  Receipt,
  Brain,
  Bot,
  Ticket,
  Activity,
  Bell,
  FileBarChart,
  Smartphone,
  BookOpen,
  Menu,
  X,
} from "lucide-react";
import { cn, API_BASE_URL } from "../lib/utils";
import { useCompanyAdminAuth } from "../contexts/CompanyAdminAuthContext";
import { companyAdminCustomersAPI } from "../lib/companyAdminApi";
import { expiredFoodsAPI } from "../lib/companyAdminApi";
import { chatApi } from "../lib/chatApi";
import { ticketsAPI } from "../lib/companyAdminApi";
import useStaffUnreadSocket from "../hooks/useStaffUnreadSocket";
import ThemeToggle from "./ThemeToggle";

const navItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/admin",
    permKey: "dashboard",
  },
  {
    icon: Bot,
    label: "AI Chats",
    path: "/admin/ai-chats",
    permKey: "ai_chats",
  },
  {
    icon: MessageSquare,
    label: "Chats",
    path: "/admin/chats",
    permKey: "chats",
  },
  {
    icon: Ticket,
    label: "Tickets",
    path: "/admin/tickets",
    permKey: "tickets",
  },
  {
    icon: Users,
    label: "Customers",
    path: "/admin/customers",
    permKey: "customers",
  },
  {
    icon: Smartphone,
    label: "App Installations",
    path: "/admin/app-installations",
    permKey: "customers",
  },
  {
    icon: BookOpen,
    label: "Recipes",
    path: "/admin/recipes",
    permKey: "customers",
  },
  {
    icon: CreditCard,
    label: "Subscriptions",
    path: "/admin/subscriptions",
    permKey: "subscriptions",
  },
  {
    icon: Receipt,
    label: "Payment History",
    path: "/admin/payment-history",
    permKey: "payment_history",
  },
];

const partnerItems = [
  {
    icon: Handshake,
    label: "Partners",
    path: "/admin/partners",
    permKey: "partners",
  },
  {
    icon: Wallet,
    label: "Withdrawals",
    path: "/admin/partners/withdrawals",
    permKey: "withdrawals",
  },
];

const settingsItem = {
  icon: Settings,
  label: "Settings",
  path: "/admin/settings",
  permKey: "settings",
};
const aiConfigItem = {
  icon: Brain,
  label: "AI Config",
  path: "/admin/ai-config",
  permKey: "ai_config",
};
const aiHealthItem = {
  icon: Activity,
  label: "AI Health",
  path: "/admin/ai-health",
  permKey: "ai_health",
};
const reportsItem = {
  icon: FileBarChart,
  label: "Reports",
  path: "/admin/reports",
  permKey: "reports",
};
const aiChatsItem = { icon: Bot, label: "AI Chats", path: "/admin/ai-chats" };

const managementItems = [
  { icon: UserCog, label: "Users", path: "/admin/users", permKey: "users" },
  {
    icon: FolderTree,
    label: "Groups",
    path: "/admin/groups",
    permKey: "groups",
  },
];

// ── Bell Notification Component ────────────────────────────────────────────────
const BellNotification = () => {
  const [total, setTotal] = React.useState(0);
  const navigate = useNavigate();

  React.useEffect(() => {
    expiredFoodsAPI
      .get()
      .then((res) => setTotal(res.data.total || 0))
      .catch(() => {});
  }, []);

  return (
    <button
      onClick={() => navigate("/admin/expired-foods")}
      className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
      data-testid="bell-notification-btn"
      title="View Expired Food Entries"
    >
      <Bell className="w-5 h-5" />
      {total > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
          {total > 99 ? "99+" : total}
        </span>
      )}
    </button>
  );
};

const CompanyAdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    company,
    user,
    logout,
    hasPermission,
    userPermissions,
    companyConfig,
  } = useCompanyAdminAuth();
  const { branding } = useBranding();
  const [chatGroups, setChatGroups] = useState([]);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const staffToken =
    typeof window !== "undefined"
      ? localStorage.getItem("company_admin_token")
      : null;
  useStaffUnreadSocket({ enabled: !!staffToken });

  // Permission helpers
  const canSee = (key) => hasPermission(key);
  const canSeeUserManagement = canSee("users") || canSee("groups");
  const canSeePartners = canSee("partners") || canSee("withdrawals");

  // Check if AI Chat is enabled from company config
  // Safe default: hide AI items while config is loading (null) or when explicitly disabled
  const isAiEnabled = companyConfig?.ai_chat === "enabled";

  // Route guard — map paths to permission keys
  const ROUTE_PERMS = {
    "/admin/chats": "chats",
    "/admin/tickets": "tickets",
    "/admin/ai-chats": "ai_chats",
    "/admin/ai-health": "ai_health",
    "/admin/reports": "reports",
    "/admin/customers": "customers",
    "/admin/app-installations": "customers",
    "/admin/recipes": "customers",
    "/admin/subscriptions": "subscriptions",
    "/admin/payment-history": "payment_history",
    "/admin/partners/withdrawals": "withdrawals",
    "/admin/partners": "partners",
    "/admin/settings": "settings",
    "/admin/ai-config": "ai_config",
    "/admin/users": "users",
    "/admin/groups": "groups",
  };
  // Find the most specific matching route
  const matchedKey = Object.keys(ROUTE_PERMS)
    .filter(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
    )
    .sort((a, b) => b.length - a.length)[0];
  const currentRouteBlocked = matchedKey && !canSee(ROUTE_PERMS[matchedKey]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const res = await chatApi.getGroupsWithUnread();
        if (!mounted) return;
        setChatGroups(res.data.groups || []);
      } catch (e) {
        if (!mounted) return;
        setChatGroups([]);
      }
    };

    const onActivity = () => load();

    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };

    load();
    const interval = setInterval(load, 30000);
    window.addEventListener("chat:activity", onActivity);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mounted = false;
      clearInterval(interval);
      window.removeEventListener("chat:activity", onActivity);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, company]);

  // Refetch group unread when user navigates to Chats so counts are fresh as soon as they open Chat
  useEffect(() => {
    if (!location.pathname.startsWith("/admin/chats")) return;
    let mounted = true;
    chatApi
      .getGroupsWithUnread()
      .then((res) => {
        if (mounted) setChatGroups(res.data.groups || []);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [location.pathname]);
  // Fetch open ticket count for sidebar badge
  useEffect(() => {
    let mounted = true;
    const fetchCount = async () => {
      try {
        const res = await ticketsAPI.count();
        if (mounted) setOpenTicketCount(res.data.count || 0);
      } catch {
        /* ignore */
      }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [user, company]);

  // Sidebar unread: real-time via socket (chat:unread_updated → chat:activity), 30s fallback poll, visibility refetch, and refetch when navigating to Chats.

  const isChatsActive = location.pathname.startsWith("/admin/chats");
  const isChatGroupSelected =
    isChatsActive && location.search.includes("group_id=");

  const chatTotalUnread = useMemo(() => {
    return (chatGroups || []).reduce(
      (sum, g) => sum + Number(g.unread_count || 0),
      0,
    );
  }, [chatGroups]);

  const logoSrc = buildBrandingUrl(branding?.app_logo || branding?.logo_url);
  const mobiloLogoSrc = buildBrandingUrl(
    branding?.app_logo_mobile || branding?.app_logo || branding?.logo_url,
  );

  const isManagementActive =
    location.pathname.startsWith("/admin/users") ||
    location.pathname.startsWith("/admin/groups");

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll while drawer is open on mobile
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  // Redirect blocked routes to dashboard
  if (currentRouteBlocked) {
    return <Navigate to="/admin" replace />;
  }

  // Redirect AI routes if AI chat is disabled by SuperAdmin
  const isAiRoute =
    location.pathname.startsWith("/admin/ai-chats") ||
    location.pathname.startsWith("/admin/ai-config");
  if (isAiRoute && !isAiEnabled) {
    return <Navigate to="/admin" replace />;
  }

  const closeMobileNav = () => setMobileNavOpen(false);

  const sidebar = (
    <>
      <div className="h-16 flex items-center justify-between px-4 border-b border-border gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={branding?.brand_name || "Logo"}
              className="h-8 max-w-[120px] object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground leading-tight truncate">
              {branding?.brand_name || "WingsDiet"}
            </div>
            <div className="text-xs text-muted-foreground leading-tight truncate max-w-[160px]">
              {company?.name || company?.email}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="hidden md:block">
            <BellNotification />
          </div>
          <button
            type="button"
            className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
            onClick={closeMobileNav}
            aria-label="Close menu"
            data-testid="mobile-nav-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 sidebar overflow-y-auto">
        {navItems
          .filter(
            (item) =>
              canSee(item.permKey) &&
              (item.path !== "/admin/ai-chats" || isAiEnabled),
          )
          .map((item) => {
            const isChats = item.path === "/admin/chats";
            const isActive = location.pathname === item.path;

            if (isChats) {
              return (
                <div key={item.path} className="mb-1">
                  <div
                    className={cn(
                      "w-full nav-item flex items-center justify-between gap-3 px-3 py-2.5 rounded-md cursor-default",
                      isChatGroupSelected
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" strokeWidth={1.5} />
                      <span
                        className={cn(
                          "text-sm",
                          isChatGroupSelected ? "font-bold" : "font-semibold",
                        )}
                      >
                        {item.label}
                      </span>
                      {chatTotalUnread ? (
                        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                          {chatTotalUnread}
                        </span>
                      ) : null}
                    </div>
                    <ChevronDown className="w-4 h-4" />
                  </div>

                  <div className="pl-2 mt-1">
                    {chatGroups.length ? (
                      chatGroups.map((g) => {
                        const active = location.search.includes(
                          `group_id=${g.id}`,
                        );
                        return (
                          <Link
                            key={g.id}
                            to={`/admin/chats?group_id=${g.id}`}
                            onClick={closeMobileNav}
                            className={cn(
                              "nav-item flex items-center justify-between gap-3 px-3 py-2 rounded-md mb-1 transition-colors text-sm font-semibold",
                              active
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <ChevronRight
                                className="w-4 h-4"
                                strokeWidth={2}
                              />
                              <span className="truncate">{g.name}</span>
                            </div>
                            {g.unread_count ? (
                              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                                {g.unread_count}
                              </span>
                            ) : null}
                          </Link>
                        );
                      })
                    ) : (
                      <div className="text-xs text-muted-foreground px-3 py-2">
                        No groups assigned
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={closeMobileNav}
                className={cn(
                  "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                  isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-semibold flex-1">
                  {item.label}
                </span>
                {item.path === "/admin/tickets" && openTicketCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                    {openTicketCount}
                  </span>
                )}
              </Link>
            );
          })}

        {canSeeUserManagement && (
          <div className="mt-4">
            <div
              className={cn(
                "flex items-center justify-between px-3 py-2.5 rounded-md cursor-default",
                isManagementActive
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
            >
              <div className="flex items-center gap-3">
                <UserCog className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-semibold">Users Management</span>
              </div>
              <ChevronDown className="w-4 h-4" />
            </div>

            <div className="pl-2 mt-1">
              {managementItems
                .filter((item) => canSee(item.permKey))
                .map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileNav}
                      className={cn(
                        "nav-item flex items-center gap-3 px-3 py-2 rounded-md mb-1 transition-colors",
                        isActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <item.icon className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-sm font-semibold">
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
            </div>
          </div>
        )}

        {canSeePartners && (
          <div className="mt-4">
            <div
              className={cn(
                "flex items-center justify-between px-3 py-2.5 rounded-md cursor-default",
                location.pathname.startsWith("/admin/partners")
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
            >
              <div className="flex items-center gap-3">
                <Handshake className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-sm font-semibold">Partners</span>
              </div>
              <ChevronDown className="w-4 h-4" />
            </div>

            <div className="pl-2 mt-1">
              {partnerItems
                .filter((item) => canSee(item.permKey))
                .map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileNav}
                      className={cn(
                        "nav-item flex items-center gap-3 px-3 py-2 rounded-md mb-1 transition-colors",
                        isActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <item.icon className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-sm font-semibold">
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
            </div>
          </div>
        )}

        <div className="mt-4">
          {canSee("settings") &&
            (() => {
              const isActive = location.pathname === settingsItem.path;
              return (
                <Link
                  to={settingsItem.path}
                  onClick={closeMobileNav}
                  className={cn(
                    "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <settingsItem.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">
                    {settingsItem.label}
                  </span>
                </Link>
              );
            })()}
          {canSee("ai_config") &&
            isAiEnabled &&
            (() => {
              const isActive = location.pathname === aiConfigItem.path;
              return (
                <Link
                  to={aiConfigItem.path}
                  onClick={closeMobileNav}
                  data-testid="nav-ai-config"
                  className={cn(
                    "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <aiConfigItem.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">
                    {aiConfigItem.label}
                  </span>
                </Link>
              );
            })()}
          {isAiEnabled &&
            canSee("ai_health") &&
            (() => {
              const isActive = location.pathname === aiHealthItem.path;
              return (
                <Link
                  to={aiHealthItem.path}
                  onClick={closeMobileNav}
                  data-testid="nav-ai-health"
                  className={cn(
                    "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <aiHealthItem.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">
                    {aiHealthItem.label}
                  </span>
                </Link>
              );
            })()}
          {canSee("reports") &&
            (() => {
              const isActive = location.pathname === reportsItem.path;
              return (
                <Link
                  to={reportsItem.path}
                  onClick={closeMobileNav}
                  data-testid="nav-reports"
                  className={cn(
                    "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <reportsItem.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">
                    {reportsItem.label}
                  </span>
                </Link>
              );
            })()}
        </div>
      </nav>

      <div className="p-3 border-t border-border space-y-2">
        <div className="px-1">
          <ThemeToggle className="w-full justify-center" />
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-destructive/10 transition-colors text-destructive"
          data-testid="company-admin-logout-btn"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm font-semibold">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center justify-between px-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="p-2 rounded-lg hover:bg-muted text-foreground shrink-0"
            aria-label="Open menu"
            data-testid="mobile-nav-open"
          >
            <Menu className="w-5 h-5" />
          </button>
          {mobiloLogoSrc || logoSrc ? (
            <img
              src={mobiloLogoSrc || logoSrc}
              alt={branding?.brand_name || "Logo"}
              className="h-7 max-w-[100px] object-contain"
            />
          ) : (
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-white" />
            </div>
          )}
          <span className="text-sm font-semibold text-foreground truncate">
            {branding?.brand_name || "WingsDiet"}
          </span>
        </div>
        <BellNotification />
      </header>

      {/* Backdrop */}
      {mobileNavOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          aria-label="Close menu overlay"
          onClick={closeMobileNav}
        />
      )}

      {/* Sidebar: drawer on mobile, static on desktop */}
      <aside
        className={cn(
          "bg-card border-r border-border flex flex-col w-64 shrink-0",
          "fixed md:static inset-y-0 left-0 z-50",
          "transition-transform duration-300 ease-in-out",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
        data-testid="company-admin-sidebar"
      >
        {sidebar}
      </aside>

      <main className="flex-1 min-w-0 flex flex-col overflow-hidden pt-14 md:pt-0">
        <div className="flex-1 p-3 sm:p-4 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </main>
    </div>
  );
};

export default CompanyAdminLayout;
